/**
 * @file lis_alert_notification_settings_screen.js
 * @description Screen for managing LIS Alert email notification settings
 *
 * Features:
 * - Display all LIS ICA alerts with notification status
 * - Enable/disable email notifications for specific alerts
 * - Bulk enable/disable notifications
 * - Filter by Business Unit, date range, notification status
 * - Pagination and searchPorca  capabilities
 *
 * Business Rules:
 * - Cannot modify MUST_ALERT if email already sent (NOTIFIED !== null)
 * - Only admin ICA users can access this screen (LIS_ALERT_ROLE - Test21)
 * - Users see only alerts from their Business Unit
 *
 * @version 1.0
 * @date 2026-01-12
 */

import React, { useEffect, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import UserContext from "../../components/User_Context.js";
import * as menu from "../../assets/menus/menu.js";
import * as breadcrumb from "../../components/ui/breadcrumb.js";
import * as api from "../../api/apiConnector.js";
import { useTranslation } from "react-i18next";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { LisAlertNotificationLabels } from "../_label/lis_labels/lis_labels.js";
import LisTableHeader from "../../components/lis/LisTableHeader.js";

const LisAlertNotificationSettingsScreen = () => {
  const { currentUser } = useContext(UserContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const MySwal = withReactContent(Swal);

  // Initialize i18n labels
  const i18n = menu.getI18n();
  i18n.addResourceBundle(
    "it",
    "lisNotificationLabels",
    LisAlertNotificationLabels.it,
    true,
    true
  );
  i18n.addResourceBundle(
    "en",
    "lisNotificationLabels",
    LisAlertNotificationLabels.en,
    true,
    true
  );

  // State management
  const [tableData, setTableData] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTestIds, setSelectedTestIds] = useState(new Set());

  // Filter states
  const [filters, setFilters] = useState({
    dateFrom: "",
    dateTo: "",
    mustAlert: null, // null=all, true=enabled, false=disabled
    notified: null, // null=all, true=notified, false=pending
  });

  // Uniform sizing consistent with DMS table toolbar
  const toolbarItemWidthPx = 130;
  const compactHeightPx = 42;
  const uniformControlStyle = {
    height: compactHeightPx,
    minHeight: compactHeightPx,
  };

  useEffect(() => {
    // Set breadcrumb
    const path = [
      { link: "/dashboard", name: "Home" },
      { link: "/admin", name: "Admin" },
      {
        link: "/admin/lis_alert_notification_settings",
        name: t("screenTitle", { ns: "lisNotificationLabels" }),
      },
    ];
    breadcrumb.set(path);

    // Write menu
    menu.write({ user: currentUser() });

    // Initial data load - show only pending notifications by default
    fetchAlertData({ page: 1, pageSize: 50 });
  }, []);

  /**
   * Fetch alert data with notification status from API
   */
  const fetchAlertData = async (params = {}) => {
    const { page = 1, pageSize = 50, ...filterParams } = params;

    try {
      setIsLoading(true);
      const userBusinessUnit = currentUser()?.BusinessUnitId ?? null;

      const response = await api.sendMessage(`admin/lis/infectionAlerts`, {
        user: currentUser(),
        body: {
          page,
          pageSize,
          businessUnit: userBusinessUnit,
          ...filterParams,
          dateFrom: filterParams.dateFrom || undefined,
          dateTo: filterParams.dateTo || undefined,
          mustAlert:
            filterParams.mustAlert === null ? null : filterParams.mustAlert,
          notified:
            filterParams.notified === null ? null : filterParams.notified,
        },
      });

      if (response.success) {
        setTableData(response.data || []);
        setPagination(response.pagination || null);
        // Clear selection when data changes
        setSelectedTestIds(new Set());
      } else {
        throw new Error(response.message || "Failed to fetch alert data");
      }
    } catch (error) {
      console.error("Error fetching alert data:", error);
      MySwal.fire({
        icon: "error",
        title: t("errorLoadingData", { ns: "lisNotificationLabels" }),
        text: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle search with filters
   */
  const handleSearch = () => {
    fetchAlertData({
      page: 1,
      pageSize: pagination?.pageSize ?? 50,
      ...filters,
    });
  };

  /**
   * Clear all filters and reload data
   */
  const clearFilters = () => {
    const resetFilters = {
      dateFrom: "",
      dateTo: "",
      mustAlert: null,
      notified: null,
    };
    setFilters(resetFilters);
    fetchAlertData({ page: 1, pageSize: pagination?.pageSize ?? 50 });
  };

  /**
   * Handle page change for pagination
   */
  const handlePageChange = (newPage) => {
    fetchAlertData({
      page: newPage,
      pageSize: pagination?.pageSize ?? 50,
      ...filters,
    });
  };

  /**
   * Toggle notification flag for a single alert
   */
  const toggleNotificationFlag = async (testId, currentValue, isNotified) => {
    // Cannot modify if already notified
    if (isNotified) {
      MySwal.fire({
        icon: "warning",
        title: t("cannotModifyTitle", { ns: "lisNotificationLabels" }),
        text: t("cannotModifyNotified", { ns: "lisNotificationLabels" }),
      });
      return;
    }

    const newValue = !currentValue;

    try {
      const response = await api.sendMessage(
        `admin/lis/infectionAlerts/setNotificationFlag`,
        {
          user: currentUser(),
          body: {
            testId,
            mustAlert: newValue,
          },
        }
      );

      if (response.success) {
        // Update local state
        setTableData((prevData) =>
          prevData.map((row) =>
            row.Test_Id === testId ? { ...row, MUST_ALERT: newValue } : row
          )
        );

        // Show success toast
        MySwal.fire({
          icon: "success",
          title: t("successTitle", { ns: "lisNotificationLabels" }),
          text: newValue
            ? t("notificationEnabled", { ns: "lisNotificationLabels" })
            : t("notificationDisabled", { ns: "lisNotificationLabels" }),
          timer: 2000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
      } else {
        throw new Error(
          response.message || "Failed to update notification flag"
        );
      }
    } catch (error) {
      console.error("Error updating notification flag:", error);
      MySwal.fire({
        icon: "error",
        title: t("errorTitle", { ns: "lisNotificationLabels" }),
        text: error.message,
      });
    }
  };

  /**
   * Toggle checkbox selection for a test
   */
  const toggleSelection = (testId) => {
    setSelectedTestIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(testId)) {
        newSet.delete(testId);
      } else {
        newSet.add(testId);
      }
      return newSet;
    });
  };

  /**
   * Toggle select all checkboxes
   */
  const toggleSelectAll = () => {
    if (selectedTestIds.size === tableData.length) {
      setSelectedTestIds(new Set());
    } else {
      setSelectedTestIds(new Set(tableData.map((row) => row.Test_Id)));
    }
  };

  /**
   * Bulk enable/disable notifications
   */
  const handleBulkUpdate = async (mustAlert) => {
    if (selectedTestIds.size === 0) {
      MySwal.fire({
        icon: "warning",
        title: t("noSelectionTitle", { ns: "lisNotificationLabels" }),
        text: t("noSelectionMessage", { ns: "lisNotificationLabels" }),
      });
      return;
    }

    // Filter out already notified alerts
    const selectedRows = tableData.filter((row) =>
      selectedTestIds.has(row.Test_Id)
    );
    const notifiedRows = selectedRows.filter((row) => row.NOTIFIED !== null);
    const validRows = selectedRows.filter((row) => row.NOTIFIED === null);

    if (notifiedRows.length > 0) {
      const proceed = await MySwal.fire({
        icon: "warning",
        title: t("bulkWarningTitle", { ns: "lisNotificationLabels" }),
        html: `
          ${t("bulkWarningMessage", { ns: "lisNotificationLabels" })}<br/>
          <strong>${t("alreadyNotified", { ns: "lisNotificationLabels" })}: ${
          notifiedRows.length
        }</strong><br/>
          <strong>${t("canBeModified", { ns: "lisNotificationLabels" })}: ${
          validRows.length
        }</strong>
        `,
        showCancelButton: true,
        confirmButtonText: t("continue", { ns: "lisNotificationLabels" }),
        cancelButtonText: t("cancel", { ns: "lisNotificationLabels" }),
      });

      if (!proceed.isConfirmed) {
        return;
      }
    }

    if (validRows.length === 0) {
      MySwal.fire({
        icon: "info",
        title: t("noValidSelectionTitle", { ns: "lisNotificationLabels" }),
        text: t("noValidSelectionMessage", { ns: "lisNotificationLabels" }),
      });
      return;
    }

    // Confirm bulk action
    const confirmResult = await MySwal.fire({
      icon: "question",
      title: t("confirmBulkTitle", { ns: "lisNotificationLabels" }),
      text: mustAlert
        ? t("confirmBulkEnable", { ns: "lisNotificationLabels" }).replace(
            "{count}",
            validRows.length
          )
        : t("confirmBulkDisable", { ns: "lisNotificationLabels" }).replace(
            "{count}",
            validRows.length
          ),
      showCancelButton: true,
      confirmButtonText: t("confirm", { ns: "lisNotificationLabels" }),
      cancelButtonText: t("cancel", { ns: "lisNotificationLabels" }),
      confirmButtonColor: mustAlert ? "#28a745" : "#dc3545",
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    try {
      MySwal.fire({
        title: t("updatingTitle", { ns: "lisNotificationLabels" }),
        text: t("updatingMessage", { ns: "lisNotificationLabels" }),
        allowOutsideClick: false,
        showConfirmButton: false,
        willOpen: () => {
          Swal.showLoading();
        },
      });

      const validTestIds = validRows.map((row) => row.Test_Id);

      const response = await api.sendMessage(
        `admin/lis/infectionAlerts/bulkSetNotificationFlag`,
        {
          user: currentUser(),
          body: {
            testIds: validTestIds,
            mustAlert,
          },
        }
      );

      if (response.success) {
        MySwal.fire({
          icon: "success",
          title: t("successTitle", { ns: "lisNotificationLabels" }),
          text: t("bulkUpdateSuccess", { ns: "lisNotificationLabels" }).replace(
            "{count}",
            response.updated || validRows.length
          ),
          timer: 3000,
          showConfirmButton: false,
        });

        // Refresh data
        fetchAlertData({
          page: pagination?.currentPage ?? 1,
          pageSize: pagination?.pageSize ?? 50,
          ...filters,
        });
      } else {
        throw new Error(
          response.message || "Failed to bulk update notifications"
        );
      }
    } catch (error) {
      console.error("Error during bulk update:", error);
      MySwal.fire({
        icon: "error",
        title: t("errorTitle", { ns: "lisNotificationLabels" }),
        text: error.message,
      });
    }
  };

  /**
   * Format date for display
   */
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("it-IT", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return dateStr;
    }
  };

  /**
   * Get notification status badge
   */
  const getNotificationBadge = (mustAlert, notified) => {
    if (notified) {
      return (
        <span
          className="badge bg-success"
          title={t("emailSent", { ns: "lisNotificationLabels" })}
        >
          <i className="fas fa-check-circle me-1"></i>
          {t("sent", { ns: "lisNotificationLabels" })}
        </span>
      );
    }
    if (mustAlert) {
      return (
        <span
          className="badge bg-warning text-dark"
          title={t("pendingEmail", { ns: "lisNotificationLabels" })}
        >
          <i className="fas fa-clock me-1"></i>
          {t("pending", { ns: "lisNotificationLabels" })}
        </span>
      );
    }
    return (
      <span
        className="badge bg-secondary"
        title={t("notificationsDisabled", { ns: "lisNotificationLabels" })}
      >
        <i className="fas fa-ban me-1"></i>
        {t("disabled", { ns: "lisNotificationLabels" })}
      </span>
    );
  };

  const allSelected =
    tableData.length > 0 && selectedTestIds.size === tableData.length;
  const someSelected =
    selectedTestIds.size > 0 && selectedTestIds.size < tableData.length;

  return (
    <>
      <div className="page-titles">
        <ol className="breadcrumb"></ol>
      </div>
      <div className="container-fluid">
        <div className="card-body p-0 bg-white">
          <div className="table-responsive active-projects task-table">
            <div className="tbl-caption h-auto">
              <div className="row">
                <div className="col-12">
                  <div className="d-flex justify-content-between align-items-center">
                    <h3 className="mb-0">
                      <i className="fas fa-bell me-2"></i>
                      {t("screenTitle", { ns: "lisNotificationLabels" })}
                    </h3>
                    <button
                      className="btn btn-sm btn-outline-info"
                      onClick={() => {
                        MySwal.fire({
                          icon: "info",
                          title: t("helpTitle", {
                            ns: "lisNotificationLabels",
                          }),
                          html: `
                    <div class="text-start">
                      <p><strong>${t("helpSection1Title", {
                        ns: "lisNotificationLabels",
                      })}</strong></p>
                      <p class="small">${t("helpSection1Text", {
                        ns: "lisNotificationLabels",
                      })}</p>
                      <p><strong>${t("helpSection2Title", {
                        ns: "lisNotificationLabels",
                      })}</strong></p>
                      <p class="small">${t("helpSection2Text", {
                        ns: "lisNotificationLabels",
                      })}</p>
                      <p><strong>${t("helpSection3Title", {
                        ns: "lisNotificationLabels",
                      })}</strong></p>
                      <p class="small">${t("helpSection3Text", {
                        ns: "lisNotificationLabels",
                      })}</p>
                    </div>
                  `,
                          width: 600,
                        });
                      }}
                    >
                      <i className="fas fa-question-circle me-1"></i>
                      {t("help", { ns: "lisNotificationLabels" })}
                    </button>
                  </div>
                  <p className="text-muted">
                    {t("subtitle", { ns: "lisNotificationLabels" })}
                  </p>
                </div>
              </div>
              <div className="">
                {/* Filters Card */}
                <LisTableHeader
                  filtersConfig={[
                    {
                      key: "dateFrom",
                      type: "date",
                      label: t("dateFrom", { ns: "lisNotificationLabels" }),
                    },
                    {
                      key: "dateTo",
                      type: "date",
                      label: t("dateTo", { ns: "lisNotificationLabels" }),
                    },
                    {
                      key: "mustAlert",
                      type: "select",
                      label: t("notificationStatus", {
                        ns: "lisNotificationLabels",
                      }),
                      options: [
                        { value: true, label: t("enabled", { ns: "lisNotificationLabels" }) },
                        { value: false, label: t("disabled", { ns: "lisNotificationLabels" }) },
                      ],
                      placeholder: t("all", { ns: "lisNotificationLabels" }),
                    },
                    {
                      key: "notified",
                      type: "select",
                      label: t("emailStatus", { ns: "lisNotificationLabels" }),
                      options: [
                        { value: false, label: t("notSent", { ns: "lisNotificationLabels" }) },
                        { value: true, label: t("sent", { ns: "lisNotificationLabels" }) },
                      ],
                      placeholder: t("all", { ns: "lisNotificationLabels" }),
                    },
                  ]}
                  values={filters}
                  onChange={(key, value) => {
                    const normalizedValue =
                      (key === "mustAlert" || key === "notified") && value === ""
                        ? null
                        : value;
                    setFilters((prev) => ({ ...prev, [key]: normalizedValue }));
                  }}
                  onSearch={handleSearch}
                  onReset={clearFilters}
                  isLoading={isLoading}
                  uniformControlStyle={uniformControlStyle}
                  columnClass="col-md-2"
                />

                {/* Bulk Actions Card */}
                {selectedTestIds.size > 0 && (
                  <div className="card mb-3 border-primary">
                    <div className="card-body py-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{selectedTestIds.size}</strong>{" "}
                          {t("selectedItems", { ns: "lisNotificationLabels" })}
                        </div>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() => handleBulkUpdate(true)}
                          >
                            <i className="fas fa-bell me-1"></i>
                            {t("enableSelected", {
                              ns: "lisNotificationLabels",
                            })}
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleBulkUpdate(false)}
                          >
                            <i className="fas fa-bell-slash me-1"></i>
                            {t("disableSelected", {
                              ns: "lisNotificationLabels",
                            })}
                          </button>
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => setSelectedTestIds(new Set())}
                          >
                            <i className="fas fa-times me-1"></i>
                            {t("clearSelection", {
                              ns: "lisNotificationLabels",
                            })}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Data Table Card */}
                <div className="card">
                  <div className="card-body">
                    {isLoading ? (
                      <div className="text-center py-5">
                        <div
                          className="spinner-border text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">
                            {t("loading", { ns: "lisNotificationLabels" })}
                          </span>
                        </div>
                      </div>
                    ) : tableData.length === 0 ? (
                      <div className="alert alert-info">
                        <i className="fas fa-info-circle me-2"></i>
                        {t("noData", { ns: "lisNotificationLabels" })}
                      </div>
                    ) : (
                      <>
                        <div
                          className="table-responsive"
                          style={{
                            maxHeight: "500px",
                            overflowX: "auto",
                            overflowY: "auto",
                          }}
                        >
                          <table className="table table-hover table-sm">
                            <thead className="table-light sticky-top">
                              <tr>
                                <th style={{ width: "40px" }}>
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={allSelected}
                                    ref={(input) => {
                                      if (input)
                                        input.indeterminate = someSelected;
                                    }}
                                    onChange={toggleSelectAll}
                                  />
                                </th>
                                <th style={{ whiteSpace: "nowrap" }}>
                                  {t("date", { ns: "lisNotificationLabels" })}
                                </th>

                                <th style={{ whiteSpace: "nowrap" }}>
                                  {t("microorganism", {
                                    ns: "lisNotificationLabels",
                                  })}
                                </th>
                                <th style={{ whiteSpace: "nowrap" }}>
                                  {t("material", {
                                    ns: "lisNotificationLabels",
                                  })}
                                </th>
                                <th style={{ whiteSpace: "nowrap" }}>
                                  {t("businessUnit", {
                                    ns: "lisNotificationLabels",
                                  })}
                                </th>
                                <th
                                  style={{
                                    whiteSpace: "nowrap",
                                    textAlign: "center",
                                  }}
                                >
                                  {t("notify", { ns: "lisNotificationLabels" })}
                                </th>
                                <th
                                  style={{
                                    whiteSpace: "nowrap",
                                    textAlign: "center",
                                  }}
                                >
                                  {t("status", { ns: "lisNotificationLabels" })}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {tableData.map((row) => {
                                const isNotified = row.NOTIFIED !== null;
                                const isSelected = selectedTestIds.has(
                                  row.Test_Id
                                );

                                return (
                                  <tr
                                    key={row.Test_Id}
                                    className={
                                      isNotified ? "table-secondary" : ""
                                    }
                                  >
                                    <td>
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={isSelected}
                                        onChange={() =>
                                          toggleSelection(row.Test_Id)
                                        }
                                      />
                                    </td>
                                    <td style={{ whiteSpace: "nowrap" }}>
                                      {formatDate(row.Test_Date)}
                                    </td>

                                    <td style={{ whiteSpace: "nowrap" }}>
                                      {row.Microorganism_Name || "-"}
                                    </td>
                                    <td style={{ whiteSpace: "nowrap" }}>
                                      {row.Material_Description || "-"}
                                    </td>
                                    <td style={{ whiteSpace: "nowrap" }}>
                                      {row.Business_Unit_Name || "-"}
                                    </td>
                                    <td style={{ textAlign: "center" }}>
                                      <div className="form-check form-switch d-flex justify-content-center">
                                        <input
                                          className="form-check-input"
                                          type="checkbox"
                                          role="switch"
                                          checked={row.MUST_ALERT || false}
                                          disabled={isNotified}
                                          onChange={() =>
                                            toggleNotificationFlag(
                                              row.Test_Id,
                                              row.MUST_ALERT,
                                              isNotified
                                            )
                                          }
                                          style={{
                                            cursor: isNotified
                                              ? "not-allowed"
                                              : "pointer",
                                          }}
                                          title={
                                            isNotified
                                              ? t("cannotModifyNotified", {
                                                  ns: "lisNotificationLabels",
                                                })
                                              : row.MUST_ALERT
                                              ? t("clickToDisable", {
                                                  ns: "lisNotificationLabels",
                                                })
                                              : t("clickToEnable", {
                                                  ns: "lisNotificationLabels",
                                                })
                                          }
                                        />
                                      </div>
                                    </td>
                                    <td style={{ textAlign: "center" }}>
                                      {getNotificationBadge(
                                        row.MUST_ALERT,
                                        row.NOTIFIED
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {/* Pagination */}
                        {pagination && pagination.totalPages > 1 && (
                          <div className="d-flex justify-content-between align-items-center mt-3">
                            <div className="text-muted small">
                              {t("page", { ns: "lisNotificationLabels" })}{" "}
                              {pagination.currentPage}{" "}
                              {t("of", { ns: "lisNotificationLabels" })}{" "}
                              {pagination.totalPages} ({pagination.totalItems}{" "}
                              {t("records", { ns: "lisNotificationLabels" })})
                            </div>
                            <nav>
                              <ul className="pagination pagination-sm mb-0">
                                <li
                                  className={`page-item ${
                                    pagination.currentPage === 1
                                      ? "disabled"
                                      : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() =>
                                      handlePageChange(
                                        pagination.currentPage - 1
                                      )
                                    }
                                    disabled={pagination.currentPage === 1}
                                  >
                                    <i className="fas fa-chevron-left"></i>
                                  </button>
                                </li>
                                {[...Array(pagination.totalPages)].map(
                                  (_, i) => {
                                    const pageNum = i + 1;
                                    if (
                                      pageNum === 1 ||
                                      pageNum === pagination.totalPages ||
                                      (pageNum >= pagination.currentPage - 2 &&
                                        pageNum <= pagination.currentPage + 2)
                                    ) {
                                      return (
                                        <li
                                          key={pageNum}
                                          className={`page-item ${
                                            pagination.currentPage === pageNum
                                              ? "active"
                                              : ""
                                          }`}
                                        >
                                          <button
                                            className="page-link"
                                            onClick={() =>
                                              handlePageChange(pageNum)
                                            }
                                          >
                                            {pageNum}
                                          </button>
                                        </li>
                                      );
                                    } else if (
                                      pageNum === pagination.currentPage - 3 ||
                                      pageNum === pagination.currentPage + 3
                                    ) {
                                      return (
                                        <li
                                          key={pageNum}
                                          className="page-item disabled"
                                        >
                                          <span className="page-link">...</span>
                                        </li>
                                      );
                                    }
                                    return null;
                                  }
                                )}
                                <li
                                  className={`page-item ${
                                    pagination.currentPage ===
                                    pagination.totalPages
                                      ? "disabled"
                                      : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() =>
                                      handlePageChange(
                                        pagination.currentPage + 1
                                      )
                                    }
                                    disabled={
                                      pagination.currentPage ===
                                      pagination.totalPages
                                    }
                                  >
                                    <i className="fas fa-chevron-right"></i>
                                  </button>
                                </li>
                              </ul>
                            </nav>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LisAlertNotificationSettingsScreen;
